import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, RotateCcw, Search } from "lucide-react";
import { useApi } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { EmptyRow } from "../components/Common";
import { AuditDetail, AuditRow, AuditSelect, actionLabel } from "../components/AuditBits";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";

const EMPTY = { source: "all", org: "all", action: "all", result: "all", q: "", suspicious: false, from: "", to: "" };

const buildQuery = (f, page) => {
  const p = new URLSearchParams({ page, page_size: 50 });
  ["source", "org", "action", "result"].forEach((k) => f[k] !== "all" && p.set(k, f[k]));
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.suspicious) p.set("suspicious", "true");
  if (f.from) p.set("date_from", new Date(f.from).toISOString());
  if (f.to) p.set("date_to", new Date(f.to).toISOString());
  return p.toString();
};

const DateInput = ({ label, value, onChange, testid }) => (
  <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
    {label}
    <Input type="datetime-local" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-52 border-slate-700 bg-slate-900 font-mono text-xs [color-scheme:dark]" />
  </label>
);

const Filters = ({ f, set, summary, onReset }) => (
  <div className="panel fade-up mb-4 flex flex-wrap items-center gap-3 p-4" data-testid="audit-filters">
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
      <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Nome, email, CitizenID, licença…"
        data-testid="audit-filter-search" className="w-64 border-slate-700 bg-slate-900 pl-9" />
    </div>
    <AuditSelect value={f.source} onChange={(v) => set("source", v)} placeholder="Todas as origens" testid="audit-filter-source"
      options={[{ value: "game", label: "Jogo" }, { value: "panel", label: "Painel" }]} />
    <AuditSelect value={f.org} onChange={(v) => set("org", v)} placeholder="Todas as organizações" testid="audit-filter-org" className="w-56"
      options={(summary?.orgs || []).map((o) => ({ value: o, label: o }))} />
    <AuditSelect value={f.action} onChange={(v) => set("action", v)} placeholder="Todas as ações" testid="audit-filter-action" className="w-52"
      options={(summary?.actions || []).map((a) => ({ value: a, label: actionLabel(a) }))} />
    <AuditSelect value={f.result} onChange={(v) => set("result", v)} placeholder="Todos os resultados" testid="audit-filter-result"
      options={[{ value: "aceite", label: "Aceite" }, { value: "recusado", label: "Recusado" }]} />
    <DateInput label="De" value={f.from} onChange={(v) => set("from", v)} testid="audit-filter-date-from" />
    <DateInput label="Até" value={f.to} onChange={(v) => set("to", v)} testid="audit-filter-date-to" />
    <label className="flex items-center gap-2 text-sm text-rose-300">
      <Switch checked={f.suspicious} onCheckedChange={(v) => set("suspicious", v)} data-testid="audit-filter-suspicious" />
      Só suspeitos
    </label>
    <Button variant="ghost" size="sm" onClick={onReset} data-testid="audit-filter-reset" className="text-slate-400 hover:text-slate-100">
      <RotateCcw className="mr-1 h-3.5 w-3.5" />Limpar
    </Button>
  </div>
);

const Pager = ({ data, page, setPage }) => (
  <div className="mt-4 flex items-center justify-between font-mono text-xs text-slate-500">
    <span data-testid="audit-total">{data?.total ?? 0} registo(s)</span>
    <div className="flex items-center gap-3">
      <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-page-prev" className="border-slate-700 bg-transparent">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span data-testid="audit-page-indicator">Página {page} de {data?.pages ?? 1}</span>
      <Button size="sm" variant="outline" disabled={!data || page >= data.pages} onClick={() => setPage(page + 1)} data-testid="audit-page-next" className="border-slate-700 bg-transparent">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

export default function AuditoriaPage() {
  const [f, setF] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => setQ(f.q), 350);
    return () => clearTimeout(t);
  }, [f.q]);
  const { data, error } = useApi(`/admin/audit?${buildQuery({ ...f, q }, page)}`, 15000);
  const { data: summary } = useApi("/admin/audit/summary", 30000);
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };

  return (
    <div>
      <PageHeader eyebrow="Registo de eventos · 90 dias" title="Auditoria">
        <span className="font-mono text-xs uppercase tracking-widest text-rose-300" data-testid="audit-suspicious-24h">
          {summary?.suspicious_24h ?? 0} suspeito(s) nas últimas 24h
        </span>
      </PageHeader>
      <Filters f={f} set={set} summary={summary} onReset={() => { setF(EMPTY); setPage(1); }} />
      {error && <div className="mb-4 border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="audit-error">{error}</div>}
      <div className="panel fade-up overflow-x-auto">
        <table className="w-full text-left" data-testid="audit-table">
          <thead className="border-b border-slate-800 font-mono text-[11px] uppercase tracking-widest text-slate-500">
            <tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Origem</th><th className="px-4 py-3">Ator</th><th className="px-4 py-3">Ação</th><th className="px-4 py-3">Zona / Alvo</th><th className="px-4 py-3">Resultado</th><th className="px-4 py-3">Motivo</th></tr>
          </thead>
          <tbody>
            {data && data.items.length === 0 && <EmptyRow cols={7} text="Nenhum registo encontrado." testid="audit-empty" />}
            {(data?.items || []).map((e) => <AuditRow key={e.id} e={e} onOpen={setOpen} />)}
          </tbody>
        </table>
      </div>
      <Pager data={data} page={page} setPage={setPage} />
      <AuditDetail entry={open} onClose={() => setOpen(null)} />
    </div>
  );
}
